import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import {
    ErrorHandlingFrontendAlertDialogComponent
} from "../components/errorhandlingfrontend-alert-dialog/errorhandlingfrontend-alert-dialog.component" ;
import { firstValueFrom } from "rxjs";
import { SmexFrontendUtils } from "../../shared/services";

@Injectable()
export class ErrorHandlingFrontendService {
    constructor(
        private m_Dialog: MatDialog
    ) {
    }

    /**
     * Trouver l’exception de base d’une erreur.
     */
    public getBaseException(exception: any): any {
        let err: any = exception;

        if (SmexFrontendUtils.isObject(err)) {
            if ("rejection" in exception) {
                err = exception["rejection"];
            }

            // Essayer de trouver l’objet "error" dans la valeur.
            if ("error" in err) {
                err = err["error"];
                if (err instanceof ArrayBuffer) {
                    err = new TextDecoder().decode(err);
                    try {
                        err = JSON.parse(err);
                    } catch (_) {
                        // do nothing
                    }
                }
                if (SmexFrontendUtils.isObject(err)) {
                    // À l’intérieur le message est probablement JSON (au moins de SmartGED)
                    if (SmexFrontendUtils.isString(err["message"])) {
                        try {
                            let MsgObj = JSON.parse(err["message"]);
                            if (SmexFrontendUtils.isObject(MsgObj)) {
                                if ("message" in MsgObj) {
                                    err = MsgObj;
                                }
                            }
                        } catch (_) {
                            err = err["message"];
                        }
                    } else {
                        // Sinon les exceptions de notre serveur SmartUsers mettent le message ici.
                        if (Array.isArray(err["errors"])) {
                            for (let err2 of err["errors"]) {
                                let msg = err2["message"];
                                if (SmexFrontendUtils.isString(msg)) {
                                    err = err2;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }

        return err;
    }

    /**
     * Déterminer si une erreur est une absence d’authentification.
     *
     * @param exception     L’erreur à tester.
     * @returns         true si c’est une erreur d’autorisation, false sinon.
     */
    public isUnauthorizedException(exception: any): boolean {
        let err = exception;
        if (typeof err == "object") {
            if ("rejection" in err) {
                err = err["rejection"];
            }
            if ("status" in err) {
                return err["status"] === 401;
            }
        }
        return false;
    }

    /**
     * Déterminer si une erreur est une absence d’autorisation.
     *
     * @param exception     L’erreur à tester.
     * @returns         true si c’est une erreur d’autorisation, false sinon.
     */
    public isForbiddenException(exception: any): boolean {
        let err = exception;
        if (typeof err == "object") {
            if ("rejection" in err) {
                err = err["rejection"];
            }
            if ("status" in err) {
                return err["status"] === 403;
            }
        }
        return false;
    }

    /**
     * Obtenir le message de base d’une erreur.
     * @param exception     L’erreur à examiner.
     * @returns         Le message de base de l’erreur.
     */
    public getErrorMessage(exception: any): string {
        const err = this.getBaseException(exception);
        if ("message" in err) {
            return err["message"];
        } else if (SmexFrontendUtils.isString(exception)) {
            return exception;
        } else {
            return "Erreur inconnue.";
        }
    }

    /**
     * Afficher une boîte de dialogue contenant un message d’erreur.
     * @param title         Le titre en haut de la boîte.
     * @param shortMessage  Un message clair et concis qui devrait être compréhensible.
     * @param fullMessage   Un message facultatif pour un expert qui donne plus de détails (comme contenu de la pile etc.).
     * @param dialog    Le service de dialogues Angular pour les cas où l’appel est avant l’initialisation de l’injecteur.
     */
    public async reportErrorMessage(
            title: string,
            shortMessage: string,
            fullMessage: string | null,
            dialog: MatDialog | null = null): Promise<void> {
        const dlgConfig = new MatDialogConfig<any>();
        dlgConfig.data =
            {
                DialogTitle       : title,
                DialogText        : shortMessage,
                CanShowFullMessage: fullMessage !== null,
                FullMessage       : fullMessage
            };

        let dlg: MatDialog;
        if (dialog === null) {
            dlg = this.m_Dialog;
        } else {
            dlg = dialog;
        }

        const dlgRef = dlg.open(ErrorHandlingFrontendAlertDialogComponent, dlgConfig);
        return firstValueFrom(dlgRef.afterClosed());
    }

    /**
     * Signaler une erreur lors d’une opération.
     * @param title     Le titre à mettre sur le message.
     * @param error     L’exception soulevée.
     * @param dialog    Le service de dialogues Angular pour les cas où l’appel est avant l’initialisation de l’injecteur.
     */
    public reportException(title: string, error: any, dialog: MatDialog | null = null): Promise<void> {

        let message: string = "Unknown error.";
        let fullMessage: string | null = null;
        if ("stack" in error) {
            fullMessage = error.stack;
        }

        // Essayer de trouver le vrai message
        const exc = this.getBaseException(error);
        if (SmexFrontendUtils.isString(exc)) {
            message = exc;
        } else if (SmexFrontendUtils.isString(error.message)) {
            message = error.message;
        } else {
            if (fullMessage !== null) {
                message = fullMessage;
                fullMessage = null;
            }
        }

        return this.reportErrorMessage(title, message, fullMessage, dialog);
    }

}

